/*
  Create according to need specific service requests 
  here to individual files in this folder
*/

import {
  IMatch,
  IMatchDetail,
  ITeamScores,
  UpcomingMatch,
  IStaff,
  ITeamInfo,
  ITeamOverview,
  IPlayerSeasonStats,
  IChampionSeasonStats,
  ISeasonStatsMeta,
  ITeamSeasonStats,
  IPlayerGameStats,
  OApiReturns,
} from "../types/types";
import { parseApiResponse } from "./utils/apiError";
import { sortSeasonsDesc } from "../utils/seasonOrder";

const api = import.meta.env.VITE_API_URL;

export const fetchApi = async <T>(url: string): Promise<T | undefined> => {
  const response = await fetch(`${api}/${url}`, {
    headers: { "Content-Type": "application/json" },
  });

  return parseApiResponse<T>(response);
};

export const fetchTimerDuration = async (): Promise<boolean | number> => {
  const response = await fetch(`${api}/matches/time?_=${String(Date.now())}`, {
    headers: { "Content-Type": "application/json" },
    cache: "no-store",
  });
  const timer = await parseApiResponse<number>(response);
  if (timer === OApiReturns.NextGameUnavailable) {
    return false;
  }
  return timer ?? 0;
};

export const fetchTeamScores = async (season?: string): Promise<ITeamScores[]> => {
  const params = season ? `?season=${encodeURIComponent(season)}` : "";
  return (await fetchApi<ITeamScores[]>(`teams/scores${params}`)) ?? [];
};

export const fetchTeamInfo = async (): Promise<ITeamInfo[]> => {
  return (await fetchApi<ITeamInfo[]>("teams")) ?? [];
};

export const fetchTeamOverview = async (teamId: string): Promise<ITeamOverview | undefined> => {
  const id = teamId.trim();
  if (!id) return undefined;
  return fetchApi<ITeamOverview>(`teams/${encodeURIComponent(id)}/overview`);
};

const normalizeSeasonStatsMeta = (
  body: ISeasonStatsMeta | string[] | undefined,
): ISeasonStatsMeta => {
  if (Array.isArray(body)) {
    const seasons = sortSeasonsDesc(body);
    return {
      seasons,
      // Legacy API has no currentSeason field — newest season is the default.
      currentSeason: seasons[0] ?? "",
    };
  }

  return {
    seasons: sortSeasonsDesc(body?.seasons ?? []),
    currentSeason: body?.currentSeason ?? "",
  };
};

export const fetchSeasonStatsMeta = async (): Promise<ISeasonStatsMeta> => {
  const meta = await fetchApi<ISeasonStatsMeta | string[]>("season-stats");
  return normalizeSeasonStatsMeta(meta);
};

export const fetchSeasons = async (): Promise<string[]> => {
  const meta = await fetchSeasonStatsMeta();
  return meta.seasons;
};

export type PlayerSeasonStatsParams = {
  season: string;
  teamId?: string;
  role?: string;
  roster?: string;
  sortKey?: string;
  direction?: "asc" | "desc";
};

export const fetchPlayerSeasonStats = async (
  seasonOrParams: string | PlayerSeasonStatsParams,
  teamId?: string,
): Promise<IPlayerSeasonStats[]> => {
  const query: PlayerSeasonStatsParams =
    typeof seasonOrParams === "string" ? { season: seasonOrParams, teamId } : seasonOrParams;

  const params = new URLSearchParams({ season: query.season });
  if (query.teamId) params.set("team_id", query.teamId);
  if (query.role) params.set("role", query.role);
  if (query.roster) params.set("roster", query.roster);
  if (query.sortKey) params.set("sort_key", query.sortKey);
  if (query.direction) params.set("direction", query.direction);
  return (await fetchApi<IPlayerSeasonStats[]>(`season-stats/players?${params.toString()}`)) ?? [];
};

export type ChampionSeasonStatsParams = {
  season: string;
  champion?: string;
  role?: string;
  sortKey?: string;
  direction?: "asc" | "desc";
};

export const fetchChampionSeasonStats = async (
  query: ChampionSeasonStatsParams,
): Promise<IChampionSeasonStats[]> => {
  const params = new URLSearchParams({ season: query.season });
  if (query.champion) params.set("champion", query.champion);
  if (query.role) params.set("role", query.role);
  if (query.sortKey) params.set("sort_key", query.sortKey);
  if (query.direction) params.set("direction", query.direction);
  return (
    (await fetchApi<IChampionSeasonStats[]>(`season-stats/champions?${params.toString()}`)) ?? []
  );
};

export const fetchTeamSeasonStats = async (
  season: string,
  statType = "per_game",
): Promise<ITeamSeasonStats[]> => {
  const params = new URLSearchParams({ season, stat_type: statType });
  return (await fetchApi<ITeamSeasonStats[]>(`season-stats/teams?${params.toString()}`)) ?? [];
};

export const fetchPlayerGameStats = async (
  teamId: string,
  playerNames: string[],
  season?: string,
): Promise<IPlayerGameStats[]> => {
  const names = [...new Set(playerNames.map((n) => n.trim()).filter(Boolean))];
  if (names.length === 0) return [];
  const params = new URLSearchParams({ player_name: names[0] });
  if (season) params.set("season", season);
  if (teamId) params.set("team_id", teamId);
  for (let i = 1; i < names.length; i++) {
    params.append("player_name_alt", names[i]);
  }
  return (
    (await fetchApi<IPlayerGameStats[]>(`season-stats/players/games?${params.toString()}`)) ?? []
  );
};

export const fetchUpcomingMatchData = async (): Promise<UpcomingMatch[]> => {
  return (await fetchApi<UpcomingMatch[]>("matches/upcoming")) ?? [];
};
export const fetchPlayoffMatchData = async (): Promise<UpcomingMatch[]> => {
  return (await fetchApi<UpcomingMatch[]>("matches/playoff")) ?? [];
};

export const fetchPlayedMatches = async () => {
  return (await fetchApi<IMatch[]>("matches/resolved")) ?? [];
};

export const fetchMatchDetail = async (matchId: number): Promise<IMatchDetail | undefined> => {
  return fetchApi<IMatchDetail>(`matches/${String(matchId)}`);
};

export const fetchStaff = async () => {
  return (await fetchApi<IStaff[]>("staff")) ?? [];
};
